import React, { useEffect, useRef, useState } from 'react';
import { useDeployTracker, type DeployTrack, DEPLOY_STAGES } from '../../contexts/DeployTrackerContext';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';

/**
 * Persistent bottom-right widget that shows every in-flight or recently
 * finished deploy. Lives at the App root so it survives route changes.
 */
export function DeployTrackerWidget(): React.JSX.Element | null {
  const { tracks, dismiss } = useDeployTracker();
  const { isDark } = useTheme();
  if (tracks.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        zIndex: 9998,
        pointerEvents: 'none',
      }}
    >
      {tracks.map(track => (
        <TrackCard key={track.slug} track={track} isDark={isDark} onDismiss={() => dismiss(track.slug)} />
      ))}
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  upload: 'Upload',
  analyze: 'Analyze',
  dockerfile: 'Dockerfile',
  build: 'Build',
  deploy: 'Deploy',
  keycloak: 'SSO',
  done: 'Live',
  failed: 'Failed',
};

interface TrackCardProps {
  track: DeployTrack;
  isDark: boolean;
  onDismiss: () => void;
}

/** One deploy card showing stage progression + expandable logs panel. */
function TrackCard({ track, isDark, onDismiss }: TrackCardProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const colors = isDark ? COLORS.dark : COLORS.light;
  const accent = track.currentStage === 'failed' ? '#EF4444' : track.currentStage === 'done' ? '#4ADE80' : '#01A9ED';
  const stageIndex = DEPLOY_STAGES.indexOf(track.currentStage === 'failed' ? 'done' : track.currentStage);
  const pct = track.currentStage === 'failed' ? 100 : Math.round((stageIndex / (DEPLOY_STAGES.length - 1)) * 100);

  return (
    <div
      style={{
        minWidth: expanded ? '520px' : '360px',
        maxWidth: expanded ? '640px' : '440px',
        padding: '14px',
        borderRadius: '12px',
        backgroundColor: colors.card,
        border: `1px solid ${accent}55`,
        boxShadow: '0 12px 32px rgba(0,0,0,0.32)',
        pointerEvents: 'auto',
        transition: 'min-width 200ms ease, max-width 200ms ease',
      }}
    >
      <Header
        accent={accent} track={track} colors={colors}
        expanded={expanded}
        onToggleExpand={() => setExpanded(v => !v)}
        onDismiss={onDismiss}
      />
      <ProgressBar pct={pct} accent={accent} isDark={isDark} />
      <StageDots track={track} accent={accent} colors={colors} />
      {track.currentStage === 'done' && <LaunchRow track={track} isDark={isDark} />}
      {track.currentStage === 'failed' && <FailureCallout message={track.errorMessage ?? 'Deployment failed'} />}
      {expanded && <LogsPanel track={track} isDark={isDark} colors={colors} />}
    </div>
  );
}

/** Title row with slug/version, expand toggle, and a close button. */
function Header({ accent, track, colors, expanded, onToggleExpand, onDismiss }: {
  accent: string;
  track: DeployTrack;
  colors: { text: string; textSecondary: string };
  expanded: boolean;
  onToggleExpand: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const stageLabel = STAGE_LABELS[track.currentStage] ?? track.currentStage;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <button
        onClick={onToggleExpand}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flex: 1, textAlign: 'left' }}
      >
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: accent, animation: track.currentStage === 'done' || track.currentStage === 'failed' ? 'none' : 'dotPulse 1.4s infinite ease-in-out' }} />
        <div style={{ fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {track.name} · v{track.version} · {stageLabel}
        </div>
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button
          onClick={onToggleExpand}
          aria-label={expanded ? 'Minimize logs' : 'Show logs'}
          title={expanded ? 'Minimize' : 'Show logs'}
          style={{ border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
        >
          {expanded ? '–' : '⌃'}
        </button>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{ border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** Scrollable pipeline log panel shown when the card is expanded. */
function LogsPanel({ track, isDark, colors }: {
  track: DeployTrack;
  isDark: boolean;
  colors: { text: string; textSecondary: string; border: string };
}): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [track.events.length]);

  return (
    <div
      ref={scrollRef}
      style={{
        marginTop: '12px',
        maxHeight: '260px',
        overflowY: 'auto',
        padding: '10px',
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        backgroundColor: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '11px',
        lineHeight: 1.55,
        color: colors.text,
      }}
    >
      {track.events.length === 0 && (
        <div style={{ color: colors.textSecondary, fontFamily: FONTS.body, fontSize: '12px' }}>
          Waiting for pipeline events…
        </div>
      )}
      {track.events.map((event, i) => (
        <LogRow key={i} event={event} colors={colors} />
      ))}
    </div>
  );
}

/** One monospace log row: time · STAGE · message. */
function LogRow({ event, colors }: {
  event: { stage: string; at: string; message: string };
  colors: { text: string; textSecondary: string };
}): React.JSX.Element {
  const time = formatEventTime(event.at);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '52px 70px 1fr', gap: '8px', padding: '2px 0' }}>
      <span style={{ color: colors.textSecondary }}>{time}</span>
      <span style={{ color: '#01A9ED', textTransform: 'uppercase' }}>{event.stage}</span>
      <span style={{ color: colors.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{event.message}</span>
    </div>
  );
}

/** Format an ISO timestamp as HH:MM:SS; returns "--" on parse failure. */
function formatEventTime(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '--';
  const date = new Date(ts);
  const pad = (value: number): string => value.toString().padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** Horizontal fill bar indicating stage progression. */
function ProgressBar({ pct, accent, isDark }: { pct: number; accent: string; isDark: boolean }): React.JSX.Element {
  return (
    <div style={{
      height: '4px',
      borderRadius: '4px',
      backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}>
      <div style={{ height: '100%', width: `${pct}%`, backgroundColor: accent, transition: 'width 300ms ease' }} />
    </div>
  );
}

/** Row of small stage pills showing which stages are done vs pending. */
function StageDots({ track, accent, colors }: {
  track: DeployTrack;
  accent: string;
  colors: { textSecondary: string };
}): React.JSX.Element {
  const activeIndex = track.currentStage === 'failed' ? DEPLOY_STAGES.length - 1 : DEPLOY_STAGES.indexOf(track.currentStage);
  return (
    <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap' }}>
      {DEPLOY_STAGES.map((stage, i) => (
        <span
          key={stage}
          style={{
            fontFamily: FONTS.heading,
            fontSize: '9px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '3px 6px',
            borderRadius: '4px',
            color: i <= activeIndex ? accent : colors.textSecondary,
            backgroundColor: i <= activeIndex ? `${accent}22` : 'transparent',
            border: `1px solid ${i <= activeIndex ? accent + '55' : 'transparent'}`,
          }}
        >
          {STAGE_LABELS[stage] ?? stage}
        </span>
      ))}
    </div>
  );
}

/** Red callout shown when a deploy fails — hoists the error text into view. */
function FailureCallout({ message }: { message: string }): React.JSX.Element {
  return (
    <div
      style={{
        marginTop: '10px',
        padding: '10px 12px',
        borderRadius: '8px',
        backgroundColor: 'rgba(239,68,68,0.12)',
        border: '1px solid rgba(239,68,68,0.4)',
        color: '#FCA5A5',
        fontFamily: FONTS.body,
        fontSize: '12px',
        lineHeight: 1.45,
      }}
    >
      <div style={{ fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '4px', color: '#EF4444' }}>
        Deployment failed
      </div>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message}</div>
    </div>
  );
}

/** "Open app" button shown after a successful deploy. */
function LaunchRow({ track, isDark }: { track: DeployTrack; isDark: boolean }): React.JSX.Element {
  return (
    <a
      href={track.appUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        marginTop: '10px',
        padding: '10px 14px',
        borderRadius: '8px',
        textAlign: 'center',
        background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
        color: '#FFF',
        fontFamily: FONTS.heading,
        fontSize: '11px',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        textDecoration: 'none',
      }}
    >
      Launch {track.name} →
    </a>
  );
}
